import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import reset from "@insite/client-framework/Store/Components/ProductSelector/Handlers/Reset";
// import searchProducts from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import setUnitOfMeasure from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetUnitOfMeasure";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@ucs/MobiusOverrides/TextField/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import searchProducts from "@ucs/Handlers/UcsSearchProducts";
import CloseRounded from "@ucs/Icons/CloseRounded";
import Search from "@ucs/Icons/Search";
import DynamicDropdown, {
    DynamicDropdownPresentationProps,
    OptionObject,
} from "@ucs/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import { listStyles } from "@ucs/Overrides/Widgets/Brand/BrandList";
import UCSColors from "@ucs/Utils/UCSColors";
import debounce from "lodash/debounce";
import React, { ChangeEvent, MutableRefObject, ReactNode, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    onSelectProduct: (productInfo: ProductInfo, product: ProductModel) => void;
    skipInventoryValidation?: boolean;
    selectButtonTitle?: string;
    productIsConfigurableMessage?: ReactNode;
    productIsUnavailableMessage?: ReactNode;
    customErrorMessage?: ReactNode;
    extendedStyles?: ProductSelectorStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const { isSearching, searchResults, selectedProductInfo, errorType } = getProductSelector(state);
    const product = getProductState(state, selectedProductInfo?.productId).value;
    return {
        isSearching,
        searchResults,
        selectedProductInfo,
        product,
        errorType,
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    searchProducts,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    reset,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductSelectorStyles {
    container?: InjectableCss;
    searchItem?: InjectableCss;
    qtyItem?: InjectableCss;
    qtyButtonFlex?: any;
    unitOfMeasureGridItem?: any;
    buttonGridItem?: any;
    unitOfMeasureSelect?: SelectPresentationProps;
    qtyTextField?: TextFieldPresentationProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
    selectButton?: ButtonPresentationProps;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    dropdownStyleProps?: any;
}

export const productSelectorStyles: ProductSelectorStyles = {
    dropdownStyleProps: {
        css: css`
            border: 2px solid ${UCSColors.gray07} !important;
        `,
    },
    qtyButtonFlex: {
        css: css`
            display: grid;
            grid-template-columns: [first] 86px [line2] 150px;
            grid-gap: 8px;
            grid-auto-flow: column;
            align-items: start;
        `,
    },
    container: {
        css: css`
            display: flex;
            flex-grow: 1;
            align-items: flex-start;
            justify-content: flex-start;
            gap: 10px;
            padding-bottom: 16px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: block;
                    `,
                    css`
                        display: block;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    searchItem: {
        css: css`
            flex: 1 auto;
        `,
    },
    qtyItem: {
        css: css`
            flex: 1;
        `,
    },
    unitOfMeasureGridItem: {
        css: css`
            flex: 1;
        `,
    },
    buttonGridItem: {
        css: css`
            flex: 1;
        `,
    },
    optionWrapper: {
        css: css`
            flex: 1;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 50px;
            height: 50px;
            margin-right: 10px;
        `,
    },
    productImage: {
        width: "50px",
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        size: 14,
        css: css`
            margin-top: 5px;
        `,
    },
    selectButton: {
        css: css`
            width: 150px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const ENTER_KEY = 13;

const ProductSelector = ({
    onSelectProduct,
    skipInventoryValidation,
    selectButtonTitle,
    searchProducts,
    isSearching,
    searchResults,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    selectedProductInfo,
    product,
    errorType,
    extendedStyles,
    customErrorMessage,
    productIsConfigurableMessage,
    productIsUnavailableMessage,
    location,
    reset,
}: Props) => {
    const [qty, setQty] = useState("1");
    const [errorMessage, setErrorMessage] = useState<ReactNode>("");
    const [selectedProductId, setSelectedProductId] = useState("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [styles] = useState(() => mergeToNew(productSelectorStyles, extendedStyles));
    const childRef = useRef<any>(null);

    useEffect(() => {
        reset();
    }, [location.pathname]);

    useEffect(() => {
        if (!searchResults) {
            return;
        }

        const newOptions = searchResults.map(product => ({
            optionText: product.title,
            optionValue: product.id || undefined,
            rowChildren: (
                <StyledWrapper {...styles.optionWrapper}>
                    <StyledWrapper {...styles.imageWrapper}>
                        <LazyImage {...styles.productImage} src={product.image} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.infoWrapper}>
                        <Typography {...styles.autocompleteTitleText}>{product.title}</Typography>
                        <Typography {...styles.autocompleteErpText}>{product.erpNumber}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
            ),
        }));
        setOptions(newOptions);
    }, [searchResults]);

    useEffect(() => {
        setErrorMessage(customErrorMessage);
    }, [customErrorMessage]);

    useEffect(() => {
        setSelectedProductId(selectedProductInfo ? selectedProductInfo.productId : "");
    }, [selectedProductInfo]);

    useEffect(() => {
        switch (errorType) {
            case "productIsConfigurable":
                setErrorMessage(productIsConfigurableMessage || translate("Cannot select configurable products"));
                break;
            case "productIsUnavailable":
                setErrorMessage(productIsUnavailableMessage || translate("Product is unavailable"));
                break;
            default:
                setErrorMessage("");
                break;
        }
    }, [errorType]);

    const onSelectionChangeHandler = (value?: string) => {
        if (searchResults && searchResults.length > 0) {
            const variantParentId = searchResults.find(o => o.id === value)?.styleParentId;
            if (variantParentId) {
                setProduct({
                    productId: variantParentId,
                    variantId: value,
                    validateProduct: true,
                    skipInventoryValidation,
                });
            } else {
                setProduct({
                    productId: value,
                    validateProduct: true,
                    skipInventoryValidation,
                });
            }
        }
    };

    const debouncedSearchProducts = debounce((query: string) => {
        searchProducts({ query });
    }, 100); // Changed as 300ms is too slow.

    const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        setErrorMessage("");
        setSearchTerm(event.target.value);
        if (selectedProductInfo) {
            clearProduct();
        }
        debouncedSearchProducts(event.target.value);
    };

    const selectProduct = () => {
        if (!selectedProductInfo || errorMessage || !product) {
            return;
        }

        const newProductInfo = { ...selectedProductInfo };
        newProductInfo.qtyOrdered = Math.max(Number(qty) || 1, product.minimumOrderQty || 1);
        onSelectProduct(newProductInfo, product);

        clearProduct();
        setQty("1");
        searchProducts({ query: "" });
    };

    const uomChangeHandler = (unitOfMeasure: string) => {
        if (!selectedProductInfo) {
            return;
        }

        setUnitOfMeasure({ unitOfMeasure });
    };

    const onKeyPress = (event: React.KeyboardEvent) => {
        if (event.charCode === ENTER_KEY) {
            if (selectedProductInfo) {
                selectProduct();
            } else if (searchTerm && (!searchResults || searchResults.length === 0)) {
                // setProduct({ searchTerm, skipInventoryValidation });
            }
        }
    };

    const qtyKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const clearSearchResults = () => {
        reset();
        setSearchTerm("");
        searchProducts({ query: "" });
        childRef!.current!.onClearDropdown("");
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="productSelector">
            <StyledWrapper {...styles.searchItem}>
                <DynamicDropdown
                    {...styles.dropdownStyleProps}
                    ref={childRef}
                    hideNoOptionsIfEmptySearch={true}
                    label={translate("Search")}
                    onSelectionChange={onSelectionChangeHandler}
                    onInputChange={onInputChanged}
                    onKeyPress={onKeyPress}
                    iconClickableProps={{ onClick: clearSearchResults }}
                    filterOption={() => true}
                    selected={selectedProductId}
                    isLoading={isSearching}
                    options={options}
                    error={errorMessage}
                    data-test-selector="productSelector_search"
                    iconProps={{ src: CloseRounded, color: UCSColors.gray07 }}
                    leftIconProps={{ src: Search, color: UCSColors.green00 }}
                    cssOverrides={{
                        formField: css`
                            input,
                            select {
                                border: 2px solid ${UCSColors.gray07} !important;
                                width: 100%;
                            }
                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(theme, [
                                    css`
                                        display: block;
                                        margin-bottom: 16px;
                                    `,
                                    css`
                                        display: block;
                                        margin-bottom: 16px;
                                    `,
                                ])}
                        `,
                        formInputWrapper: css``,
                    }}
                />
            </StyledWrapper>

            <Hidden above="sm">
                {product && selectedProductInfo ? (
                    <StyledWrapper {...styles.unitOfMeasureGridItem}>
                        <ProductContext.Provider
                            value={{
                                product,
                                productInfo: selectedProductInfo,
                                onUnitOfMeasureChanged: uomChangeHandler,
                            }}
                        >
                            <ProductUnitOfMeasureSelect
                                extendedStyles={styles.unitOfMeasureSelect}
                                data-test-selector="productSelector_uom"
                            />
                        </ProductContext.Provider>
                    </StyledWrapper>
                ) : (
                    <></>
                )}
            </Hidden>

            <Hidden below="md">
                <StyledWrapper {...styles.container}>
                    {product && selectedProductInfo ? (
                        <StyledWrapper {...styles.unitOfMeasureGridItem}>
                            <ProductContext.Provider
                                value={{
                                    product,
                                    productInfo: selectedProductInfo,
                                    onUnitOfMeasureChanged: uomChangeHandler,
                                }}
                            >
                                <ProductUnitOfMeasureSelect
                                    extendedStyles={styles.unitOfMeasureSelect}
                                    data-test-selector="productSelector_uom"
                                />
                            </ProductContext.Provider>
                        </StyledWrapper>
                    ) : (
                        <></>
                    )}
                    <StyledWrapper {...styles.qtyItem}>
                        <TextField
                            type="number"
                            label={translate("QTY")}
                            value={qty}
                            onKeyPress={qtyKeyPressHandler}
                            onChange={(e: any) => {
                                setQty(e.currentTarget.value);
                            }}
                            data-test-selector="productSelector_qty"
                            cssOverrides={{
                                formField: css`
                                    input,
                                    select {
                                        border: 2px solid ${UCSColors.gray07} !important;
                                    }
                                `,
                            }}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.buttonGridItem}>
                        <Button
                            {...styles.selectButton}
                            onClick={() => selectProduct()}
                            disabled={!selectedProductInfo || !!errorMessage || Number(qty) < 1}
                            data-test-selector="productSelector_selectProduct"
                            style={{ marginTop: "32px" }}
                        >
                            {selectButtonTitle?.toUpperCase() || translate("Select Product").toUpperCase()}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>
            <Hidden above="sm">
                <StyledWrapper {...styles.qtyButtonFlex}>
                    <TextField
                        type="number"
                        label={translate("QTY")}
                        value={qty}
                        onKeyPress={qtyKeyPressHandler}
                        onChange={(e: any) => {
                            setQty(e.currentTarget.value);
                        }}
                        data-test-selector="productSelector_qty"
                        cssOverrides={{
                            formField: css`
                                input,
                                select {
                                    border: 2px solid ${UCSColors.gray07} !important;
                                }
                            `,
                        }}
                    />
                    <Button
                        {...styles.selectButton}
                        onClick={() => selectProduct()}
                        disabled={!selectedProductInfo || !!errorMessage || Number(qty) < 1}
                        data-test-selector="productSelector_selectProduct"
                        style={{ marginTop: "30px" }}
                    >
                        {selectButtonTitle?.toUpperCase() || translate("Select Product").toUpperCase()}
                    </Button>
                </StyledWrapper>
            </Hidden>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductSelector);
